import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { EIESession } from "@alphahub/eie-web";
import {
  CALIBRATION_THEMES,
  CALIBRATION_WINDOW_PALETTES,
  makeCaptureSessionId,
} from "./eie/eieConstants";
import { getCalibrationPercent } from "./eie/eieSelectors";
import { EIEProductionView } from "./eie/EIEProductionView";
import { usePersistentCaptureMetadata } from "./eie/hooks/usePersistentCaptureMetadata";
import { useCameraCapability } from "./eie/hooks/useCameraCapability";
import { useEmotionThemeBridge } from "./eie/hooks/useEmotionThemeBridge";
import { useIntroVideoController } from "./eie/hooks/useIntroVideoController";
import { useTelemetryCapture } from "./eie/hooks/useTelemetryCapture";
import { useCameraStreamController } from "./eie/hooks/useCameraStreamController";
import { useCalibrationPanelController } from "./eie/hooks/useCalibrationPanelController";

const ENABLE_REMOTE_CSV_EXPORT = import.meta.env.VITE_EIE_REMOTE_CSV_EXPORT === 'true';

const TELEMETRY_DIAGNOSTIC_SKIP_KEYS = new Set([
  'emotion',
  'confidence',
  'yawnDetected',
  'yawnScore',
  'handNearFace',
  'handNearMouth',
  'blendshapes',
]);

function extractTelemetryDiagnostics(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return {};
  const diagnostics = {};
  for (const [key, value] of Object.entries(snapshot)) {
    if (TELEMETRY_DIAGNOSTIC_SKIP_KEYS.has(key)) continue;
    const kind = typeof value;
    if (kind === 'string' || kind === 'number' || kind === 'boolean') {
      diagnostics[`diag_${key}`] = value;
    }
  }
  return diagnostics;
}

export default function EIEDemo({
  onEmotionTheme,
  onDemoStart,
  onDemoStop,
  heroStartSignal = 0,
  badgeAccent = "#8B5CF6",
  widgetConfig = null,
}) {
  const [state, setState] = useState(null);
  const [stableEmotion, setStableEmotion] = useState(null);
  const [lastDetectedEmotion, setLastDetectedEmotion] = useState(null);
  const [cognitiveDebug, setCognitiveDebug] = useState(null);
  const [showPuzzle, setShowPuzzle] = useState(false);
  const [siteTheme, setSiteTheme] = useState(() => {
    if (typeof document !== 'undefined') return document.documentElement.getAttribute('data-theme') || 'passion';
    return 'passion';
  });
  const [puzzleConsent, setPuzzleConsent] = useState(false);
  const [isCalibrated, setIsCalibrated] = useState(false);
  const [calibrationFeedback, setCalibrationFeedback] = useState(null);
  const [headCalibrationBaseline, setHeadCalibrationBaseline] = useState(null);
  const [calibrationProgress, setCalibrationProgress] = useState(null);
  const [isCalibrationComplete, setIsCalibrationComplete] = useState(false);
  const [gazeCalibrationDone, setGazeCalibrationDone] = useState(false);
  const [emotionCalibrationDone, setEmotionCalibrationDone] = useState(false);
  const [emotionCalibrationProgress, setEmotionCalibrationProgress] = useState(0);
  const [calibrationFaceDetected, setCalibrationFaceDetected] = useState(true);
  const [calibrationColorIndex, setCalibrationColorIndex] = useState(0);

  const [isActive, setIsActive] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [classifierEngine, setClassifierEngine] = useState(() => localStorage.getItem('eie:localClassifier') || 'local');
  const [error, setError] = useState(null);
  const [heartRateData, setHeartRateData] = useState(null);
  const sessionRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const introVideoRef = useRef(null);
  const videoCalibrationDurationRef = useRef(null);
  const [isFirstTimeVideo, setIsFirstTimeVideo] = useState(() => (typeof localStorage === 'undefined' ? true : !localStorage.getItem('eie:videoWatchedAt')));
  const [showVideoOverlay, setShowVideoOverlay] = useState(false);
  const [holdOverlayUntilVideoEnd, setHoldOverlayUntilVideoEnd] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [productionFlowStarted, setProductionFlowStarted] = useState(false);
  const [productionOverlayDissolving, setProductionOverlayDissolving] = useState(false);
  const [productionPrivacyAccepted, setProductionPrivacyAccepted] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem('eie:productionPrivacyAccepted') === 'true';
  });
  const captureTelemetryEnabled = ENABLE_REMOTE_CSV_EXPORT;
  const { cameraAvailable, setCameraAvailable, cameraCapabilityChecked } = useCameraCapability();
  const {
    captureSessionId,
    setCaptureSessionId,
    captureLabel,
    setCaptureLabel,
    captureTestType,
    setCaptureTestType,
    captureDeviceTag,
    setCaptureDeviceTag,
    captureLightingTag,
    setCaptureLightingTag,
    captureProject,
    setCaptureProject,
    captureOperator,
    setCaptureOperator,
    captureSessionIdRef,
    captureLabelRef,
    captureTestTypeRef,
    captureDeviceTagRef,
    captureLightingTagRef,
    captureProjectRef,
    captureOperatorRef,
  } = usePersistentCaptureMetadata();
  const previousClassifierEngineRef = useRef(classifierEngine);
  const puzzleConsentRef = useRef(puzzleConsent);
  const calibrationCompleteRef = useRef(isCalibrationComplete);
  const hasTriggeredFirstThemeRef = useRef(false);
  const onDemoStartRef = useRef(onDemoStart);
  const onDemoStopRef = useRef(onDemoStop);
  const showPuzzleRef = useRef(showPuzzle);
  const stoppingRef = useRef(false);
  const lastHandledHeroStartSignalRef = useRef(0);
  const holdOverlayUntilVideoEndRef = useRef(false);
  const latestCognitiveRef = useRef(null);
  const runtimeVerificationLoggedRef = useRef(false);

  const {
    telemetryRowsRef,
    telemetryCount,
    recordFrame,
    recordEvent,
    syncTelemetryCount,
    clearTelemetry,
    downloadTelemetryCsv,
  } = useTelemetryCapture({
    captureSessionIdRef,
    captureLabelRef,
    captureTestTypeRef,
    captureDeviceTagRef,
    captureLightingTagRef,
    captureProjectRef,
    captureOperatorRef,
  });
  const {
    onEmotionThemeRef,
    lastAppliedStableEmotionRef,
    clearStableThemeTimer,
  } = useEmotionThemeBridge({
    onEmotionTheme,
    stableEmotion,
    isActive,
    isCalibrationComplete,
  });
  const {
    setVideoAudioBlocked,
    showUnmuteCTA,
    setShowUnmuteCTA,
    setIsVideoPlaying,
    stopIntroPlayback,
  } = useIntroVideoController({ introVideoRef });
  const { lightLevelRef, startCamera } = useCameraStreamController({
    videoRef,
    streamRef,
    setVideoReady,
    setCameraAvailable,
    setError,
  });
  useEffect(() => {
    onDemoStartRef.current = onDemoStart;
  }, [onDemoStart]);

  useEffect(() => {
    onDemoStopRef.current = onDemoStop;
  }, [onDemoStop]);

  useEffect(() => {
    puzzleConsentRef.current = puzzleConsent;
  }, [puzzleConsent]);

  useEffect(() => {
    showPuzzleRef.current = showPuzzle;
  }, [showPuzzle]);

  useEffect(() => {
    calibrationCompleteRef.current = isCalibrationComplete;
  }, [isCalibrationComplete]);

  // Signal calibration state to CSS so mobile can suspend GPU-heavy animations
  useEffect(() => {
    const el = document.documentElement;
    if (isActive && !isCalibrationComplete) {
      el.setAttribute('data-eie-calibrating', 'true');
    } else {
      el.removeAttribute('data-eie-calibrating');
    }
    return () => el.removeAttribute('data-eie-calibrating');
  }, [isActive, isCalibrationComplete]);

  // Both gaze and emotion calibration must complete before UI considers calibration done
  useEffect(() => {
    if (gazeCalibrationDone && emotionCalibrationDone) {
      setIsCalibrationComplete(true);
      // Trigger initial neutral theme only after both calibrations complete
      setLastDetectedEmotion((prev) => prev || 'neutral');
      if (!hasTriggeredFirstThemeRef.current) {
        hasTriggeredFirstThemeRef.current = true;
        onEmotionThemeRef.current?.('neutral', { force: true, emotionLabel: 'neutral' });
        lastAppliedStableEmotionRef.current = 'neutral';
      }
    }
  }, [gazeCalibrationDone, emotionCalibrationDone]);

  useEffect(() => {
    holdOverlayUntilVideoEndRef.current = holdOverlayUntilVideoEnd;
  }, [holdOverlayUntilVideoEnd]);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('eie:productionPrivacyAccepted', productionPrivacyAccepted ? 'true' : 'false');
  }, [productionPrivacyAccepted]);


  useEffect(() => {
    if (typeof document === 'undefined') return;
    const observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-theme') {
          setSiteTheme(document.documentElement.getAttribute('data-theme') || 'passion');
          break;
        }
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const currentCalibrationColors = CALIBRATION_THEMES[siteTheme] || CALIBRATION_THEMES.passion;
  const calibrationWindowPalette = CALIBRATION_WINDOW_PALETTES[siteTheme] || CALIBRATION_WINDOW_PALETTES.passion;
  const activeCalibrationAccent = currentCalibrationColors[calibrationColorIndex] || currentCalibrationColors[0];

  useEffect(() => {
    if (!showPuzzle || isCalibrationComplete) return;
    const id = window.setInterval(() => {
      setCalibrationColorIndex(prev => (prev + 1) % currentCalibrationColors.length);
    }, 2500);
    return () => window.clearInterval(id);
  }, [showPuzzle, isCalibrationComplete, currentCalibrationColors.length]);

  const getCalibrationScreenAnchor = useCallback(() => {
    const target = document.getElementById('calibration-target');
    if (!target || typeof window === 'undefined') return { x: 0.5, y: 0.5 };
    const rect = target.getBoundingClientRect();
    return {
      x: (rect.left + rect.width / 2) / window.innerWidth,
      y: (rect.top + rect.height / 2) / window.innerHeight,
    };
  }, []);

  const activeScreenAnchor = calibrationProgress?.screenAnchor || calibrationFeedback?.screenAnchor || null;

  const configuredCalibrationDurationMs = useMemo(() => {
    const value = Number(widgetConfig?.localClassifier?.calibrationDuration);
    if (!Number.isFinite(value)) return 1200;
    return Math.max(300, Math.round(value));
  }, [widgetConfig]);

  const handleSkipIntro = useCallback(() => {
    try {
      introVideoRef.current?.pause();
    } catch {
      // ignore
    }

    recordEvent('video_skipped');

    // Mark guide as watched so it won't show again
    if (typeof localStorage !== 'undefined') localStorage.setItem('eie:videoWatchedAt', String(Date.now()));
    setIsFirstTimeVideo(false);

    stopIntroPlayback(true);
    setShowVideoOverlay(false);
    setHoldOverlayUntilVideoEnd(false);

    // If calibration is already complete, close the calibration overlay entirely.
    if (calibrationCompleteRef.current) {
      setShowPuzzle(false);
    }
  }, [stopIntroPlayback, recordEvent]);

  const start = useCallback(async (calibrationDurationMs) => {
    if (!videoRef.current) return;
    try {
      setError(null);
      setCalibrating(true);

      // Build adaptive camera config based on measured room brightness.
      // avgBrightness: 0-255 (0 = pitch black, 255 = fully saturated white).
      // dark < 60  → full boost; 60-110 → partial; > 110 → no boost (pass raw video)
      const brightness = lightLevelRef.current;
      const needsBoost = brightness !== null && brightness < 110;
      const useGrayscale = brightness !== null && brightness < 55;
      const boostStrength = brightness !== null
        ? Math.max(0, Math.min(1, (110 - brightness) / (110 - 30))) // 0..1
        : 0;
      // In dim conditions, camera hardware AE needs ~1.5s to stabilise after first frame.
      // Delay calibration start so baseline samples are collected on a properly-exposed image.
      const calibrationDelay = needsBoost ? Math.round(boostStrength * 1800) : 0; // 0–1800 ms
      const runtimeIsMobile = (() => {
        if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
        const ua = navigator.userAgent || '';
        const uaMobile = /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
        const coarsePointer = window.matchMedia?.('(pointer: coarse)')?.matches === true;
        const touchCapable = typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
        return uaMobile || (coarsePointer && touchCapable);
      })();
      const cameraConfig = {
        lowLightEnhancement: needsBoost,
        brightnessBoost: needsBoost ? 1 + 0.5 * boostStrength : 1, // 1.0 – 1.5
        contrastBoost: needsBoost ? 1 + 0.25 * boostStrength : 1,  // 1.0 – 1.25
        calibrationDelay,
        // Grayscale helps in very dark scenes, but can remove useful texture in moderate light.
        grayscaleMode: useGrayscale,
        // Keep detection permissive during calibration in low-light conditions.
        minFaceDetectionConfidence: 0.10,
        minFacePresenceConfidence: 0.10,
        minTrackingConfidence: 0.10,
        isMobileRuntime: runtimeIsMobile,
      };
      console.log(`[EIE][RUNTIME-CHECK] isMobileRuntime=${runtimeIsMobile} ua=${navigator.userAgent || 'unknown'}`);
      console.log(`[EIE][LIGHT] auto camera config:`, cameraConfig, `(avgBrightness=${brightness?.toFixed(1) ?? 'unknown'})`);

      const session = new EIESession({
        apiUrl: "",
        authToken: "",
        analysisMode: "light",
        features: { facialEmotion: true, voiceAnalysis: false, heartRate: true },
        heartRate: {
          enabled: true,
          calibrationDuration: Number(widgetConfig?.heartRate?.calibrationDuration) || 60,
        },
        intervals: {
          emotion: 200,
        },
        localClassifier: {
          engine: 'local',
          suppressWhenUnengaged: false,
          calibrationDuration: calibrationDurationMs ?? configuredCalibrationDurationMs,
        },
        camera: cameraConfig,
        videoSource: videoRef.current,
      });

      if (captureTelemetryEnabled && typeof window !== 'undefined') {
        (window).__EIE_CAPTURE_FRAME = (row) => {
          recordFrame(row);
        };
      }

      session.on("emotionUpdate", (s) => {
        if (!runtimeVerificationLoggedRef.current) {
          runtimeVerificationLoggedRef.current = true;
          console.log(
            `[EIE][RUNTIME-CHECK] first-emotion-update isMobileRuntime=${cameraConfig.isMobileRuntime === true} emotion=${s?.emotion ?? 'none'} yawnDetected=${s?.yawnDetected === true}`,
          );
        }
        if (captureTelemetryEnabled && s && typeof s === 'object') {
          const diagnostics = extractTelemetryDiagnostics(s);
          const cognitiveSnapshot = latestCognitiveRef.current;
          const headRotation =
            (s?.headRotation && typeof s.headRotation === 'object' ? s.headRotation : null)
            || (cognitiveSnapshot?.headRotation && typeof cognitiveSnapshot.headRotation === 'object' ? cognitiveSnapshot.headRotation : null);
          const headPitch = Number(headRotation?.pitch);
          const headYaw = Number(headRotation?.yaw);
          const enrichedBlendshapes =
            s?.blendshapes && typeof s.blendshapes === 'object'
              ? {
                  ...s.blendshapes,
                  ...(Number.isFinite(headPitch) ? { _headPitch: headPitch } : {}),
                  ...(Number.isFinite(headYaw) ? { _headYaw: headYaw } : {}),
                }
              : undefined;
          const row = {
            ts: Date.now(),
            winner: s?.emotion,
            winnerScore: s?.confidence,
            yawnDetected: s?.yawnDetected,
            yawnScore: s?.yawnScore,
            handNearFace: s?.handNearFace,
            handNearMouth: s?.handNearMouth,
            diag_headPitch: Number.isFinite(headPitch) ? headPitch : undefined,
            diag_headYaw: Number.isFinite(headYaw) ? headYaw : undefined,
            ...diagnostics,
            diagJson: Object.keys(diagnostics).length ? JSON.stringify(diagnostics) : undefined,
            facBlendshapes: enrichedBlendshapes,
            facJson: enrichedBlendshapes ? JSON.stringify(enrichedBlendshapes) : undefined,
          };
          recordFrame(row);
        }

        if (puzzleConsentRef.current && !calibrationCompleteRef.current) {
          setCalibrating(true);
          return;
        }
        // Keep emotion output hidden until the calibration panel is closed.
        if (showPuzzleRef.current) {
          setCalibrating(true);
          return;
        }
        setState(s);
        if (s?.emotion) {
          setLastDetectedEmotion(s.emotion);
        }
        setCalibrating(false);
      });
      session.on('cognitiveUpdate', (payload) => {
        latestCognitiveRef.current = payload || null;
        setCognitiveDebug(payload || null);
      });
      session.on('calibrationStarted', () => {
        hasTriggeredFirstThemeRef.current = false;
        lastAppliedStableEmotionRef.current = null;
        setIsCalibrationComplete(false);
        setGazeCalibrationDone(false);
        setEmotionCalibrationDone(false);
        setEmotionCalibrationProgress(0);
        setCalibrationProgress({
          waitingForFace: true,
          sampleCount: 0,
          elapsedMs: 0,
          durationMs: videoCalibrationDurationRef.current || configuredCalibrationDurationMs,
          minSamples: 12,
        });
      });
      session.on('calibrationProgress', (payload) => {
        setCalibrationProgress(payload || null);
      });
      session.on('calibrationFinished', (payload) => {
        setCalibrationFeedback({
          finished: true,
          offset: payload.offset,
          medianOffset: payload.medianOffset,
          tolerance: payload.tolerance,
          headOffset: payload.headOffset,
          samples: payload.samples,
          keptSamples: payload.keptSamples,
          screenAnchor: payload.screenAnchor,
        });
        setCalibrationProgress(null);
        if (payload.headOffset) {
          setHeadCalibrationBaseline(payload.headOffset);
        }
        const calibrationOk = !!payload?.offset;
        setGazeCalibrationDone(calibrationOk);
        if (calibrationOk) {
          // If replaying guide, keep the panel open until the video ends.
          if (!holdOverlayUntilVideoEndRef.current) {
            stopIntroPlayback(true);
            setShowVideoOverlay(false);
            setShowPuzzle(false);
          }
          setIsCalibrated(true);
        } else {
          window.setTimeout(() => {
            if (!puzzleConsentRef.current) return;
            sessionRef.current?.startLocalCalibration?.(getCalibrationScreenAnchor());
          }, 450);
        }
        setTimeout(() => setCalibrationFeedback(null), 1500);
      });
      session.on('emotionCalibrationProgress', (payload) => {
        setEmotionCalibrationProgress(payload.progress ?? 0);
        setCalibrationFaceDetected(payload.faceDetected !== false);
        if (payload.progress >= 1) {
          setEmotionCalibrationDone(true);
        }
      });
      session.on('stableEmotion', (e) => {
        if (puzzleConsentRef.current && !calibrationCompleteRef.current) {
          return;
        }
        if (showPuzzleRef.current) {
          return;
        }
        const emotion = e?.emotion ?? null;
        setStableEmotion(emotion);
      });
      session.on('heartRateUpdate', (payload) => {
        if (!payload || typeof payload !== 'object') return;
        setHeartRateData(prev => {
          const hasValidBpm = typeof payload.bpm === 'number' && Number.isFinite(payload.bpm) && payload.bpm > 0;
          if (hasValidBpm) return payload;
          return prev;
        });
      });
      sessionRef.current = session;
      session.on("error", (e) => setError(e.message || "Unknown error"));
      await session.start();
      setIsActive(true);
      syncTelemetryCount();
      onDemoStartRef.current?.();
    } catch (e) {
      setError(e.message);
      setCalibrating(false);
    }
  }, [classifierEngine, getCalibrationScreenAnchor, captureTelemetryEnabled, stopIntroPlayback, recordFrame, syncTelemetryCount, configuredCalibrationDurationMs, widgetConfig]);

  const stop = useCallback(async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;

    try {
      if (sessionRef.current) {
        try {
          await sessionRef.current.stop();
        } catch (e) {
          console.warn('Failed to stop EIE session cleanly', e);
        }
        sessionRef.current = null;
      }

      setIsActive(false);
      hasTriggeredFirstThemeRef.current = false;
      setState(null);
      setStableEmotion(null);
      lastAppliedStableEmotionRef.current = null;
      clearStableThemeTimer();
      setLastDetectedEmotion(null);
      setCognitiveDebug(null);
      setHeartRateData(null);
      setCalibrating(false);
      setIsCalibrationComplete(false);
      setGazeCalibrationDone(false);
      setEmotionCalibrationDone(false);
      setEmotionCalibrationProgress(0);
      setCalibrationFaceDetected(true);
      setCalibrationProgress(null);
      setCalibrationFeedback(null);
      setProductionOverlayDissolving(false);
      setProductionFlowStarted(false);
      setVideoReady(false);
      setShowPuzzle(false);
      setIsCalibrated(false);
      syncTelemetryCount();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      stopIntroPlayback(true);
      setShowVideoOverlay(false);
      setHoldOverlayUntilVideoEnd(false);
      setShowUnmuteCTA(false);
      setIsVideoPlaying(false);
      videoCalibrationDurationRef.current = null;
      if (captureTelemetryEnabled && typeof window !== 'undefined' && window.__EIE_CAPTURE_FRAME) {
        delete window.__EIE_CAPTURE_FRAME;
      }
      onDemoStopRef.current?.();
    } finally {
      stoppingRef.current = false;
    }
  }, [captureTelemetryEnabled, stopIntroPlayback, clearStableThemeTimer, syncTelemetryCount]);

  const { openCalibrationPanel, startProductionExperience } = useCalibrationPanelController({
    streamRef,
    startCamera,
    setShowPuzzle,
    isActive,
    isFirstTimeVideo,
    videoCalibrationDurationRef,
    setShowVideoOverlay,
    setHoldOverlayUntilVideoEnd,
    setShowUnmuteCTA,
    setVideoAudioBlocked,
    setCalibrating,
    setIsCalibrationComplete,
    setCalibrationProgress,
    configuredCalibrationDurationMs,
    start,
    sessionRef,
    getCalibrationScreenAnchor,
    introVideoRef,
    recordEvent,
    setIsVideoPlaying,
    setIsFirstTimeVideo,
    stopIntroPlayback,
    cameraAvailable,
    productionFlowStarted,
    productionPrivacyAccepted,
    setError,
    setProductionFlowStarted,
  });

  const handlePrivacyAcceptedStart = useCallback(async () => {
    setProductionPrivacyAccepted(true);
    await openCalibrationPanel();
  }, [openCalibrationPanel]);

  const rotateCaptureSession = useCallback(() => {
    setCaptureSessionId(makeCaptureSessionId());
  }, [setCaptureSessionId]);

  useEffect(() => {
    localStorage.setItem('eie:localClassifier', classifierEngine);
  }, [classifierEngine]);

  // restart only when the classifier engine actually changes while active
  useEffect(() => {
    const previousEngine = previousClassifierEngineRef.current;
    previousClassifierEngineRef.current = classifierEngine;

    if (previousEngine === classifierEngine || !isActive) {
      return;
    }

    (async () => {
      try {
        await stop();
        await start();
      } catch (e) {
        console.warn('Failed to restart session after classifier change', e);
      }
    })();
  }, [classifierEngine, isActive, start, stop]);

  // Assign stream to video element after React mounts it
  useEffect(() => {
    if (isActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isActive]);

  useEffect(() => () => {
    if (sessionRef.current) {
      sessionRef.current.stop();
      onDemoStopRef.current?.();
    }
    if (captureTelemetryEnabled && typeof window !== 'undefined' && window.__EIE_CAPTURE_FRAME) {
      delete window.__EIE_CAPTURE_FRAME;
    }
  }, [captureTelemetryEnabled]);

  useEffect(() => {
    if (!heroStartSignal) return;
    if (heroStartSignal <= lastHandledHeroStartSignalRef.current) return;
    lastHandledHeroStartSignalRef.current = heroStartSignal;
    startProductionExperience();
  }, [heroStartSignal, startProductionExperience]);

  const preferTimeBasedCalibrationProgress = !!(
    (productionFlowStarted && !isCalibrationComplete) ||
    showVideoOverlay ||
    ((videoCalibrationDurationRef.current || 0) > configuredCalibrationDurationMs && !isCalibrationComplete)
  );

  const badgeEmotion = stableEmotion || state?.emotion || lastDetectedEmotion || null;
  const badgeCalibrating = calibrating || (!!calibrationProgress && !isCalibrationComplete);
  const calibrationPercent = (() => {
    if (isCalibrationComplete) return 100;
    if (emotionCalibrationProgress > 0) {
      return Math.max(1, Math.min(99, Math.round(emotionCalibrationProgress * 100)));
    }
    return 0;
  })();

  const productionCalibrationVisible = productionFlowStarted && (!isCalibrationComplete || holdOverlayUntilVideoEnd);
  const showProductionCalibrationOverlay = productionCalibrationVisible || productionOverlayDissolving;

  useEffect(() => {
    if (productionCalibrationVisible) {
      setProductionOverlayDissolving(false);
      return;
    }

    if (productionFlowStarted) {
      setProductionOverlayDissolving(true);
      const id = window.setTimeout(() => setProductionOverlayDissolving(false), 680);
      return () => window.clearTimeout(id);
    }

    setProductionOverlayDissolving(false);
    return undefined;
  }, [productionCalibrationVisible, productionFlowStarted]);

  return (
    <EIEProductionView
      isCalibrationComplete={isCalibrationComplete}
      badgeEmotion={badgeEmotion}
      state={state}
      isActive={isActive}
      badgeCalibrating={badgeCalibrating}
      badgeAccent={badgeAccent}
      heartRateBpm={heartRateData?.bpm}
      showProductionCalibrationOverlay={showProductionCalibrationOverlay}
      calibrationWindowPalette={calibrationWindowPalette}
      productionCalibrationVisible={productionCalibrationVisible}
      showVideoOverlay={showVideoOverlay}
      introVideoRef={introVideoRef}
      activeCalibrationAccent={activeCalibrationAccent}
      showUnmuteCTA={showUnmuteCTA}
      setShowUnmuteCTA={setShowUnmuteCTA}
      setVideoAudioBlocked={setVideoAudioBlocked}
      telemetryRowsRef={telemetryRowsRef}
      captureSessionIdRef={captureSessionIdRef}
      calibrationPercent={calibrationPercent}
      calibrationFaceDetected={calibrationFaceDetected}
      openCalibrationPanel={openCalibrationPanel}
      productionPrivacyAccepted={productionPrivacyAccepted}
      setProductionPrivacyAccepted={setProductionPrivacyAccepted}
      onPrivacyAcceptedStart={handlePrivacyAcceptedStart}
      onSkipIntro={handleSkipIntro}
      productionFlowStarted={productionFlowStarted}
      enableRemoteCsvExport={ENABLE_REMOTE_CSV_EXPORT}
      downloadTelemetryCsv={downloadTelemetryCsv}
      telemetryCount={telemetryCount}
      clearTelemetry={clearTelemetry}
      captureSessionId={captureSessionId}
      rotateCaptureSession={rotateCaptureSession}
      stop={stop}
      videoRef={videoRef}
    />
  );
}
